/**
 * Audio Buffer Management Service
 * Stores recorded audio locally until upload is confirmed successful
 * Enables download functionality for failed uploads
 */

interface StoredRecording {
  blob: Blob
  timestamp: Date
  exchangeId?: string
  mimeType: string
  size: number
  // Persistent recovery fields
  conversationId?: string
  messageId?: string
  uploadFailed?: boolean
  errorMessage?: string
}

// Persistent storage metadata (no blob - too large for localStorage)
interface PersistentRecordingMeta {
  recordingId: string
  timestamp: string
  conversationId?: string
  messageId?: string
  mimeType: string
  size: number
  uploadFailed: boolean
  errorMessage?: string
}

export class AudioBufferManager {
  private recordings = new Map<string, StoredRecording>()
  private readonly MAX_RECORDINGS = 10 // Prevent memory bloat
  private readonly MAX_AGE_HOURS = 24 // Auto-cleanup after 24 hours
  private readonly STORAGE_KEY = 'storyflame_failed_audio_meta'

  /**
   * Store recorded audio blob with unique identifier
   */
  storeRecording(recordingId: string, audioBlob: Blob, exchangeId?: string): void {
    // Cleanup old recordings first
    this.cleanupOldRecordings()

    const recording: StoredRecording = {
      blob: audioBlob,
      timestamp: new Date(),
      exchangeId,
      mimeType: audioBlob.type || 'audio/webm',
      size: audioBlob.size
    }

    this.recordings.set(recordingId, recording)

    // Enforce max recordings limit
    if (this.recordings.size > this.MAX_RECORDINGS) {
      const oldestKey = Array.from(this.recordings.keys())[0]
      this.recordings.delete(oldestKey)
    }

    console.log(`[AudioBufferManager] Stored recording ${recordingId} (${Math.round(audioBlob.size / 1024)}KB)`)
  }

  /**
   * Get stored recording by ID
   */
  getRecording(recordingId: string): StoredRecording | null {
    return this.recordings.get(recordingId) || null
  }

  /**
   * Clear recording from buffer (call when upload succeeds)
   */
  clearRecording(recordingId: string): void {
    const recording = this.recordings.get(recordingId)
    if (recording) {
      this.recordings.delete(recordingId)
      // Also remove from persistent storage
      this.removePersistentMeta(recordingId)
      console.log(`[AudioBufferManager] Cleared recording ${recordingId}`)
    }
  }

  /**
   * Mark recording as failed and persist metadata for cross-session recovery
   */
  markRecordingFailed(recordingId: string, errorMessage: string, conversationId?: string, messageId?: string): void {
    const recording = this.recordings.get(recordingId)
    if (!recording) {
      console.warn(`[AudioBufferManager] Cannot mark unknown recording ${recordingId} as failed`)
      return
    }

    // Update in-memory recording
    recording.uploadFailed = true
    recording.errorMessage = errorMessage
    recording.conversationId = conversationId
    recording.messageId = messageId

    // Persist metadata for cross-session recovery (not blob - too large)
    const meta: PersistentRecordingMeta = {
      recordingId,
      timestamp: recording.timestamp.toISOString(),
      conversationId,
      messageId,
      mimeType: recording.mimeType,
      size: recording.size,
      uploadFailed: true,
      errorMessage
    }

    this.savePersistentMeta(meta)
    console.log(`[AudioBufferManager] Marked recording ${recordingId} as failed - persisted for recovery`)
  }

  /**
   * Get all failed recordings that can be recovered (includes persisted from previous sessions)
   */
  getFailedRecordings(): Array<{ recordingId: string, meta: PersistentRecordingMeta, hasBlob: boolean }> {
    const failed: Array<{ recordingId: string, meta: PersistentRecordingMeta, hasBlob: boolean }> = []
    
    // Get persisted failed recordings
    const persistedMetas = this.getPersistentMetas()
    
    for (const meta of persistedMetas) {
      const hasBlob = this.recordings.has(meta.recordingId)
      failed.push({
        recordingId: meta.recordingId,
        meta,
        hasBlob
      })
    }

    return failed
  }

  /**
   * Check if any failed recordings exist (for UI notifications)
   */
  hasFailedRecordings(): boolean {
    return this.getFailedRecordings().length > 0
  }

  /**
   * Download recording as file
   */
  downloadRecording(recordingId: string, customFilename?: string): boolean {
    const recording = this.recordings.get(recordingId)
    if (!recording) {
      console.error(`[AudioBufferManager] Recording ${recordingId} not found`)
      return false
    }

    try {
      const url = URL.createObjectURL(recording.blob)
      const link = document.createElement('a')
      
      // Generate filename
      const timestamp = recording.timestamp.toISOString().slice(0, 19).replace(/[:T]/g, '-')
      const extension = this.getFileExtension(recording.mimeType)
      const filename = customFilename || `storyflame-audio-${timestamp}${extension}`
      
      link.href = url
      link.download = filename
      link.style.display = 'none'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Cleanup object URL
      setTimeout(() => URL.revokeObjectURL(url), 100)
      
      console.log(`[AudioBufferManager] Downloaded recording ${recordingId} as ${filename}`)
      return true
      
    } catch (error) {
      console.error(`[AudioBufferManager] Download failed for ${recordingId}:`, error)
      return false
    }
  }

  /**
   * Get recording info for display
   */
  getRecordingInfo(recordingId: string): { size: string; duration?: string; format: string } | null {
    const recording = this.recordings.get(recordingId)
    if (!recording) return null

    return {
      size: this.formatFileSize(recording.size),
      format: recording.mimeType,
      // Duration would require audio analysis - skip for now
    }
  }

  /**
   * Check if recording exists
   */
  hasRecording(recordingId: string): boolean {
    return this.recordings.has(recordingId)
  }

  /**
   * Get all stored recording IDs
   */
  getStoredRecordingIds(): string[] {
    return Array.from(this.recordings.keys())
  }

  /**
   * Generate support email with recording context
   */
  contactSupport(recordingId: string, errorDetails: string): void {
    const recording = this.recordings.get(recordingId)
    if (!recording) {
      console.error(`[AudioBufferManager] Cannot contact support - recording ${recordingId} not found`)
      return
    }

    const subject = encodeURIComponent(`StoryFlame Audio Upload Issue - ${recordingId}`)
    const body = encodeURIComponent(`Dear StoryFlame Support,

I experienced an issue with audio transcription:

Recording ID: ${recordingId}
Exchange ID: ${recording.exchangeId || 'Not available'}
Error: ${errorDetails}
Timestamp: ${recording.timestamp.toISOString()}
File Format: ${recording.mimeType}
File Size: ${this.formatFileSize(recording.size)}
Browser: ${navigator.userAgent}

I have downloaded the audio file that failed to upload and will attach it to this email.

Please help resolve this issue.

Thanks!`)
    
    const mailtoUrl = `mailto:support@storyflame.io?subject=${subject}&body=${body}`
    
    try {
      window.location.href = mailtoUrl
      console.log(`[AudioBufferManager] Opened support email for recording ${recordingId}`)
    } catch (error) {
      console.error(`[AudioBufferManager] Failed to open support email:`, error)
      // Fallback - copy to clipboard
      navigator.clipboard?.writeText(`support@storyflame.io\n\nSubject: ${decodeURIComponent(subject)}\n\n${decodeURIComponent(body)}`)
    }
  }

  /**
   * Cleanup recordings older than MAX_AGE_HOURS
   */
  private cleanupOldRecordings(): void {
    const cutoffTime = new Date(Date.now() - (this.MAX_AGE_HOURS * 60 * 60 * 1000))
    
    for (const [id, recording] of this.recordings.entries()) {
      if (recording.timestamp < cutoffTime) {
        this.recordings.delete(id)
        console.log(`[AudioBufferManager] Cleaned up old recording ${id}`)
      }
    }
  }

  /**
   * Get file extension from MIME type
   */
  private getFileExtension(mimeType: string): string {
    const mimeMap: Record<string, string> = {
      'audio/webm': '.webm',
      'audio/mp3': '.mp3',
      'audio/mpeg': '.mp3',
      'audio/wav': '.wav',
      'audio/wave': '.wav',
      'audio/ogg': '.ogg',
      'audio/m4a': '.m4a',
      'audio/mp4': '.m4a',
      'audio/aac': '.aac'
    }
    
    return mimeMap[mimeType.split(';')[0]] || '.audio'
  }

  /**
   * Format file size for display
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B'
    
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  /**
   * Save recording metadata to localStorage for persistent recovery
   */
  private savePersistentMeta(meta: PersistentRecordingMeta): void {
    try {
      const existing = this.getPersistentMetas()
      const updated = existing.filter(m => m.recordingId !== meta.recordingId)
      updated.push(meta)
      
      // Keep only recent failed recordings (prevent localStorage bloat)
      const recent = updated
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, this.MAX_RECORDINGS)
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(recent))
      console.log(`[AudioBufferManager] Persisted metadata for ${meta.recordingId}`)
    } catch (error) {
      console.warn('[AudioBufferManager] Failed to persist recording metadata:', error)
    }
  }

  /**
   * Get all persistent recording metadata from localStorage
   */
  private getPersistentMetas(): PersistentRecordingMeta[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (!stored) return []
      
      const metas: PersistentRecordingMeta[] = JSON.parse(stored)
      
      // Filter out old recordings
      const cutoffTime = new Date(Date.now() - (this.MAX_AGE_HOURS * 60 * 60 * 1000))
      const recent = metas.filter(meta => new Date(meta.timestamp) > cutoffTime)
      
      // Update localStorage if we filtered any out
      if (recent.length !== metas.length) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(recent))
      }
      
      return recent
    } catch (error) {
      console.warn('[AudioBufferManager] Failed to read persistent metadata:', error)
      return []
    }
  }

  /**
   * Remove recording metadata from localStorage
   */
  private removePersistentMeta(recordingId: string): void {
    try {
      const existing = this.getPersistentMetas()
      const filtered = existing.filter(m => m.recordingId !== recordingId)
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered))
      console.log(`[AudioBufferManager] Removed persistent metadata for ${recordingId}`)
    } catch (error) {
      console.warn('[AudioBufferManager] Failed to remove persistent metadata:', error)
    }
  }
}

// Export singleton instance
export const audioBufferManager = new AudioBufferManager()